/**
 * Impersonation Modal Component
 * 
 * Modal version of the impersonation interface that allows Owner users 
 * to impersonate other roles for testing and support purposes.
 */

import React, { useState } from 'react';
import { User, AlertTriangle, RefreshCw, Key, Shield, Eye, X, Users, UserCheck } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';

interface ImpersonationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ImpersonationModal: React.FC<ImpersonationModalProps> = ({ isOpen, onClose }) => {
  const { user, startImpersonation, stopImpersonation, startAudienceImpersonation } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Default to audience tab if user can't do role impersonation but can do audience impersonation
  const defaultTab = user?.impersonation?.canImpersonate ? 'roles' : 'audience';
  const [activeTab, setActiveTab] = useState<'roles' | 'audience'>(defaultTab);

  // Only render if open and user can impersonate (roles) or impersonate audience
  if (!isOpen || !(user?.impersonation?.canImpersonate || user?.impersonation?.canImpersonateAudience)) {
    return null;
  }

  const { impersonation } = user;
  const availableRoles = impersonation.availableRoles || [];

  const handleRoleChange = async (targetRole: string) => {
    if (targetRole === impersonation.effectiveRole) {
      return; // No change needed
    }

    setIsLoading(true);
    setError(null);

    try {
      if (targetRole === impersonation.actualRole) {
        // Stop impersonation (return to actual role)
        await stopImpersonation();
      } else {
        // Start new impersonation
        await startImpersonation(targetRole);
      }
      // Close modal after successful change
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change role');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAudienceImpersonation = async (targetAudience: 'customer' | 'stop') => {
    setIsLoading(true);
    setError(null);

    try {
      if (targetAudience === 'stop') {
        // Stop audience impersonation
        await stopImpersonation();
      } else {
        // Start audience impersonation
        await startAudienceImpersonation(targetAudience);
      }
      // Close modal after successful change
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change audience');
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Key size={14} className="text-blue-500" />;
      case 'admin':
        return <Shield size={14} className="text-blue-500" />;
      case 'treeline_user':
        return <User size={14} className="text-gray-500" />;
      default:
        return <User size={14} className="text-gray-400" />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return 'Owner';
      case 'admin':
        return 'Admin';
      case 'treeline_user':
        return 'Treeline User';
      default:
        return role;
    }
  };

  const allRoles = [impersonation.actualRole, ...availableRoles];

  return (
    <>
      {/* Modal Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
        onClick={onClose}
      >
        {/* Modal Content */}
        <div 
          className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <Eye size={20} className="text-blue-500" />
              <h2 className="text-lg font-semibold text-gray-900">Impersonation</h2>
              {impersonation.isImpersonating && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                  Active
                  {impersonation.effectiveAudience && ` (${impersonation.effectiveAudience})`}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Tabs - only show if both capabilities exist */}
          {impersonation.canImpersonate && impersonation.canImpersonateAudience && (
            <div className="px-6 pt-4">
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('roles')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'roles'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Shield size={16} />
                    <span>Role Impersonation</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('audience')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'audience'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Users size={16} />
                    <span>Audience Filter</span>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Modal Body */}
          <div className="p-6">
            {impersonation.isImpersonating && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <div className="flex items-center space-x-2">
                  <AlertTriangle size={14} className="text-amber-500" />
                  <span className="text-sm text-amber-700">
                    Currently impersonating{' '}
                    {impersonation.effectiveAudience ? (
                      <strong>{impersonation.effectiveAudience} user</strong>
                    ) : (
                      <strong>{getRoleLabel(impersonation.effectiveRole)}</strong>
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Role Impersonation Tab */}
            {(activeTab === 'roles' || !impersonation.canImpersonateAudience) && impersonation.canImpersonate && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Role to Experience:
                </label>
                
                <div className="space-y-2">
                  {allRoles.map((role) => {
                    const isActive = role === impersonation.effectiveRole && !impersonation.effectiveAudience;
                    const isActualRole = role === impersonation.actualRole;
                    
                    return (
                      <button
                        key={role}
                        onClick={() => handleRoleChange(role)}
                        disabled={isLoading || isActive}
                        className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-all ${
                          isActive
                            ? 'bg-blue-50 border-blue-300 text-blue-700 shadow-sm'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <div className="flex items-center space-x-3">
                          {getRoleIcon(role)}
                          <div>
                            <div className="font-medium flex items-center">
                              {getRoleLabel(role)}
                              {isActualRole && (
                                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                                  Your Role
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              {role === 'owner' && 'Full system access'}
                              {role === 'admin' && 'Can manage teaching content'}
                              {role === 'treeline_user' && 'Basic user access'}
                            </div>
                          </div>
                        </div>
                        
                        {isLoading && role !== impersonation.effectiveRole && (
                          <RefreshCw size={14} className="animate-spin text-blue-500" />
                        )}
                        
                        {isActive && (
                          <div className="flex items-center space-x-1">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                            <span className="text-xs font-medium">Active</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Audience Impersonation Tab */}
            {(activeTab === 'audience' || !impersonation.canImpersonate) && impersonation.canImpersonateAudience && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Test Content Filtering by Audience:
                </label>
                
                <div className="space-y-2">
                  {/* Internal User (No Filter) */}
                  <button
                    onClick={() => handleAudienceImpersonation('stop')}
                    disabled={isLoading || (!impersonation.effectiveAudience && !impersonation.isImpersonating)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-all ${
                      !impersonation.effectiveAudience && !impersonation.isImpersonating
                        ? 'bg-blue-50 border-blue-300 text-blue-700 shadow-sm'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <div className="flex items-center space-x-3">
                      <Shield size={14} className="text-blue-500" />
                      <div>
                        <div className="font-medium">Internal User (No Filter)</div>
                        <div className="text-xs text-gray-500">
                          Can see all content including internal Freshdesk solutions
                        </div>
                      </div>
                    </div>
                    
                    {!impersonation.effectiveAudience && !impersonation.isImpersonating && (
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        <span className="text-xs font-medium">Active</span>
                      </div>
                    )}
                  </button>

                  {/* Customer User (Filtered) */}
                  <button
                    onClick={() => handleAudienceImpersonation('customer')}
                    disabled={isLoading || impersonation.effectiveAudience === 'customer'}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-all ${
                      impersonation.effectiveAudience === 'customer'
                        ? 'bg-blue-50 border-blue-300 text-blue-700 shadow-sm'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <div className="flex items-center space-x-3">
                      <UserCheck size={14} className="text-green-500" />
                      <div>
                        <div className="font-medium">Customer User (Filtered)</div>
                        <div className="text-xs text-gray-500">
                          Only sees customer-facing content (no internal docs)
                        </div>
                      </div>
                    </div>
                    
                    {isLoading && impersonation.effectiveAudience !== 'customer' && (
                      <RefreshCw size={14} className="animate-spin text-blue-500" />
                    )}
                    
                    {impersonation.effectiveAudience === 'customer' && (
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        <span className="text-xs font-medium">Active</span>
                      </div>
                    )}
                  </button>
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-blue-700">
                    <strong>Audience Filtering Test:</strong> This allows you to test content filtering 
                    without changing your role permissions. Customer filtering will hide internal 
                    Freshdesk solutions and other internal content from chat responses.
                  </p>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center space-x-2">
                  <AlertTriangle size={14} className="text-red-500" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              </div>
            )}

            <div className="mt-6 p-3 bg-gray-50 rounded-md">
              <p className="text-xs text-gray-600">
                <strong>Note:</strong> {activeTab === 'roles' 
                  ? 'Role impersonation allows you to experience the system with different permission levels for testing and support purposes. Your actual security permissions remain unchanged.'
                  : 'Audience filtering allows you to test content visibility as different user types. This helps verify that internal content is properly filtered for external users.'
                } Sessions expire after 4 hours of inactivity.
              </p>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end px-6 py-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImpersonationModal;